import { useEffect, useRef } from 'react';
import { LatLngBounds, Map as LeafletMap } from 'leaflet';
import { TRACKING_FIT_BOUNDS_OPTIONS } from '@features/components/LocationTracking/utils/bounds.utils';

type FitStageEnum = 0 | 1 | 2;

const FitStage = { None: 0, Fallback: 1, Tracking: 2 } as const satisfies Record<string, FitStageEnum>;

/**
 * Frames the route once on mount and once more when live tracking data first arrives, so a late
 * response still gets a sensible view. The stage only ever moves forward, which stops the map
 * snapping back to the route while the user is panning around it.
 */
export const useTrackingMapFit = (
  map: LeafletMap | null,
  bounds: LatLngBounds | null,
  isTrackingActive: boolean,
): void => {
  const fittedStageRef = useRef<FitStageEnum>(FitStage.None);

  useEffect(() => {
    if (!map || !bounds?.isValid()) return;

    const stage = isTrackingActive ? FitStage.Tracking : FitStage.Fallback;
    if (stage <= fittedStageRef.current) return;

    fittedStageRef.current = stage;
    map.fitBounds(bounds, TRACKING_FIT_BOUNDS_OPTIONS);
  }, [map, bounds, isTrackingActive]);
};
